import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowDownLeft, ArrowLeft, ArrowRightLeft, ArrowUpRight, UserMinus } from 'lucide-react';
import { toast } from 'sonner';
import type { Asset, Employee, HolderInput } from '@khadi/shared';
import { api, ApiError, type ListResponse } from '../lib/api';
import { useAuth } from '../lib/auth';
import { SOURCE_LABELS, formatDate, formatDateTime } from '../lib/format';
import { DataPoint, EmptyState, PageHeader, Spinner, StatusPill, cn } from '../components/ui';
import { HolderPicker, isHolderComplete } from '../components/HolderPicker';
import { useEmployeeMovements, useLocations, type MovementEntry } from '../lib/queries';

/**
 * Personenseite mit Sammelübergabe — der Austrittsfall.
 * Alle Geräte auf einmal weiterreichen, statt jedes einzeln zu buchen.
 */
export function EmployeeDetailPage() {
  const params = useParams({ strict: false }) as { employeeId?: string };
  const employeeId = Number(params.employeeId);
  const auth = useAuth();
  const [handoverOpen, setHandoverOpen] = useState(false);

  const employee = useQuery({
    queryKey: ['employee', employeeId],
    queryFn: () => api<Employee>(`/employees/${employeeId}`),
    enabled: employeeId > 0,
  });

  const assets = useQuery({
    queryKey: ['employee-assets', employeeId],
    queryFn: () =>
      api<ListResponse<Asset>>('/assets', { query: { employee_id: employeeId, limit: 200 } }),
    enabled: employeeId > 0,
  });

  if (employee.isLoading) return <Spinner label="Person wird geladen …" />;
  if (!employee.data) return null;

  const person = employee.data;

  return (
    <div>
      <Link
        to="/personen"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-[var(--color-fg3)] no-underline hover:text-[var(--color-accent)]"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        Alle Personen
      </Link>

      <PageHeader
        eyebrow={person.is_active ? 'Mitarbeiter' : 'Ausgeschieden'}
        title={person.name}
        subtitle={person.email ?? undefined}
        actions={
          auth.can('asset.move') &&
          (assets.data?.rows.length ?? 0) > 0 && (
            <button
              type="button"
              onClick={() => setHandoverOpen(true)}
              className="btn btn-accent btn-md"
            >
              <ArrowRightLeft className="h-4 w-4" aria-hidden />
              Geräte übergeben
            </button>
          )
        }
      />

      <div className="grid gap-4 lg:grid-cols-[20rem_1fr]">
        <section className="surface animate-fade-up h-fit pad">
          {/* Der Standort der Person bestimmt, wo ihre Geräte verortet werden.
              Deshalb ist er hier direkt änderbar statt hinter einem Formular. */}
          {auth.can('employee.write') && <StandortWahl employee={person} />}

          <dl className="grid grid-cols-2 gap-4">
            <DataPoint label="Standort">{person.location?.name}</DataPoint>
            <DataPoint label="Personalnummer">{person.employee_no}</DataPoint>
            <DataPoint label="Geräte">
              <span className="tabular">{person.assets_count}</span>
            </DataPoint>
            <DataPoint label="Status">
              {person.is_active ? 'aktiv' : `ausgeschieden ${formatDate(person.exit_date)}`}
            </DataPoint>
            {person.user_id && (
              <div className="col-span-2">
                <span className="pill pill-deployed">hat auch ein Systemkonto</span>
              </div>
            )}
          </dl>

          {person.notes && (
            <p className="mt-4 border-t border-[var(--color-line)] pt-4 text-sm whitespace-pre-wrap text-[var(--color-fg2)]">
              {person.notes}
            </p>
          )}
        </section>

        <section>
          <h2 className="font-[family-name:var(--font-display)] mb-4 text-2xl">
            Zugewiesene Geräte
          </h2>

          {assets.isLoading && <Spinner />}

          {assets.data?.rows.length === 0 && (
            <EmptyState
              title="Keine Geräte zugewiesen"
              description="Dieser Person ist derzeit nichts übergeben."
            />
          )}

          <ul className="stagger space-y-3">
            {assets.data?.rows.map((asset) => (
              <li key={asset.id}>
                <Link
                  to="/geraete/$assetId"
                  params={{ assetId: String(asset.id) }}
                  className="surface card-link flex items-center justify-between gap-3 px-5 py-4 no-underline"
                >
                  <span className="min-w-0">
                    <span className="tabular block text-sm font-semibold">{asset.asset_tag}</span>
                    <span className="block truncate text-[0.9375rem]">{asset.model?.name}</span>
                    <span className="tabular block truncate text-sm text-[var(--color-fg3)]">
                      {asset.serial}
                    </span>
                  </span>
                  <StatusPill name={asset.status.name} archived={asset.status.is_archived} />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <PersonVerlauf employeeId={employeeId} />

      {handoverOpen && (
        <HandoverSheet
          employee={person}
          assets={assets.data?.rows ?? []}
          onClose={() => setHandoverOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Standort der Person setzen — freiwillig.
 *
 * Ohne Eintrag gelten die Geräte dieser Person als „beim Mitarbeiter", und das
 * ist bei ortsungebundener Arbeit die richtige Auskunft. Ein Standort lohnt
 * sich nur, wenn die Person tatsächlich fest an einem Ort sitzt — dann erben
 * ihre Geräte ihn sofort.
 */
function StandortWahl({ employee }: { employee: Employee }) {
  const queryClient = useQueryClient();
  const locations = useLocations();

  const setzen = useMutation({
    mutationFn: (locationId: number | null) =>
      api(`/employees/${employee.id}`, { method: 'PATCH', body: { location_id: locationId } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employee', employee.id] });
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Standort gespeichert — die Geräte dieser Person sind damit verortet');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen'),
  });

  return (
    <div className="mb-5 border-b border-[var(--color-line)] pb-5">
      <label className="label" htmlFor="employee-location">
        Standort
      </label>
      <select
        id="employee-location"
        className="field"
        value={employee.location?.id ?? 0}
        onChange={(event) => setzen.mutate(Number(event.target.value) || null)}
        disabled={setzen.isPending}
      >
        <option value={0}>— nicht zugeordnet —</option>
        {locations.data?.rows.map((location) => (
          <option key={location.id} value={location.id}>
            {location.name}
          </option>
        ))}
      </select>
      {employee.assets_count > 0 && !employee.location && (
        <p className="mt-2 text-sm text-[var(--color-fg3)]">
          Ohne Eintrag gelten die {employee.assets_count}{' '}
          {employee.assets_count === 1 ? 'Gerät' : 'Geräte'} dieser Person als „beim Mitarbeiter" —
          bei ortsungebundener Arbeit ist das die richtige Angabe.
        </p>
      )}
    </div>
  );
}

/** Sammelübergabe: Geräte wählen, Ziel wählen, optional Austritt vermerken. */
function HandoverSheet({
  employee,
  assets,
  onClose,
}: {
  employee: Employee;
  assets: Asset[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<number[]>(assets.map((asset) => asset.id));
  const [target, setTarget] = useState<HolderInput>({ type: 'none' });
  const [deactivate, setDeactivate] = useState(false);
  const [note, setNote] = useState('');

  const move = useMutation({
    mutationFn: () =>
      api<{ moved: number }>(`/employees/${employee.id}/move-all`, {
        method: 'POST',
        body: {
          to: target,
          asset_ids: selected,
          note: note.trim() || undefined,
          deactivate,
          dry_run: false,
        },
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['employee', employee.id] });
      void queryClient.invalidateQueries({ queryKey: ['employee-assets', employee.id] });
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success(`${result.moved} ${result.moved === 1 ? 'Gerät' : 'Geräte'} übergeben`);
      onClose();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Die Übergabe ist fehlgeschlagen'),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[var(--color-khadi-forest-900)]/45 backdrop-blur-[2px] sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Geräte übergeben"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="animate-sheet-up safe-bottom max-h-[92dvh] w-full max-w-md overflow-y-auto rounded-t-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-bg-elevated)] p-5 shadow-[var(--shadow-lg)] sm:rounded-[var(--radius-xl)]">
        <h2 className="font-[family-name:var(--font-display)] mb-1 text-xl">
          Geräte von {employee.name}
        </h2>
        <p className="mb-4 text-sm text-[var(--color-fg3)]">
          Alle gewählten Geräte werden in einem Vorgang übergeben.
        </p>

        <div className="mb-4 space-y-2 rounded-[var(--radius-md)] border border-[var(--color-line)] p-2">
          {assets.map((asset) => (
            <label
              key={asset.id}
              className={cn(
                'flex cursor-pointer items-center gap-2.5 rounded-[var(--radius-sm)] px-2 py-1.5 text-sm transition-colors',
                selected.includes(asset.id) ? 'bg-[var(--color-bg-tinted)]' : '',
              )}
            >
              <input
                type="checkbox"
                checked={selected.includes(asset.id)}
                onChange={() =>
                  setSelected((current) =>
                    current.includes(asset.id)
                      ? current.filter((id) => id !== asset.id)
                      : [...current, asset.id],
                  )
                }
                className="h-4 w-4 accent-[var(--color-accent)]"
              />
              <span className="tabular font-semibold">{asset.asset_tag}</span>
              <span className="truncate text-[var(--color-fg2)]">{asset.model?.name}</span>
            </label>
          ))}
        </div>

        <span className="label">Neues Ziel</span>
        <HolderPicker value={target} onChange={setTarget} />

        <div className="mt-4">
          <label className="label" htmlFor="handover-note">
            Notiz <span className="font-normal normal-case">(optional)</span>
          </label>
          <input
            id="handover-note"
            className="field"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="z. B. Austritt zum Monatsende"
          />
        </div>

        <label className="mt-3 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={deactivate}
            onChange={(event) => setDeactivate(event.target.checked)}
            className="mt-0.5 h-4 w-4 accent-[var(--color-accent)]"
          />
          <span>
            <span className="inline-flex items-center gap-1.5">
              <UserMinus className="h-3.5 w-3.5" aria-hidden />
              Person als ausgeschieden markieren
            </span>
            <span className="block text-sm text-[var(--color-fg3)]">
              Die Gerätehistorie bleibt vollständig erhalten.
            </span>
          </span>
        </label>

        <div className="mt-5 flex gap-2">
          <button type="button" onClick={onClose} className="btn btn-ghost btn-lg flex-1">
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => move.mutate()}
            disabled={selected.length === 0 || !isHolderComplete(target) || move.isPending}
            className="btn btn-primary btn-lg flex-1"
          >
            {move.isPending ? 'Läuft …' : `${selected.length} übergeben`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Verlauf einer Person: was sie bekommen hat und was von ihr abgegangen ist.
 *
 * Die Liste der aktuell zugewiesenen Geräte beantwortet nur „was hat sie jetzt".
 * Bei Rückläufern und Austritten zählt aber, wohin ein Gerät gegangen ist und
 * wer den Vorgang gebucht hat. Beides steht in `movements` — hier aus Sicht der
 * Person gelesen statt aus Sicht des Geräts.
 */
function PersonVerlauf({ employeeId }: { employeeId: number }) {
  const verlauf = useEmployeeMovements(employeeId);

  if (verlauf.isLoading) {
    return (
      <section className="mt-4">
        <Spinner label="Verlauf wird geladen …" />
      </section>
    );
  }

  const zeilen = verlauf.data?.rows ?? [];

  return (
    <section className="surface animate-fade-up pad mt-4" style={{ animationDelay: '80ms' }}>
      <div className="mb-5 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-[family-name:var(--font-display)] text-2xl">Verlauf</h2>
        {zeilen.length > 0 && (
          <span className="text-sm text-[var(--color-fg3)]">
            <span className="tabular">{verlauf.data?.total ?? zeilen.length}</span> Vorgänge
          </span>
        )}
      </div>

      {zeilen.length === 0 ? (
        <EmptyState
          title="Noch keine Vorgänge"
          description="Sobald dieser Person ein Gerät übergeben oder abgenommen wird, steht es hier."
        />
      ) : (
        <ul className="stagger space-y-3">
          {zeilen.map((vorgang) => (
            <li key={vorgang.id}>
              <VerlaufZeile vorgang={vorgang} employeeId={employeeId} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function VerlaufZeile({
  vorgang,
  employeeId,
}: {
  vorgang: MovementEntry;
  employeeId: number;
}) {
  // Aus Sicht der Person: Stand sie am Ziel der Bewegung, hat sie das Gerät
  // bekommen; stand sie am Anfang, ist es von ihr abgegangen.
  const erhalten = vorgang.to?.type === 'employee' && vorgang.to.id === employeeId;
  const gegenstelle = erhalten ? vorgang.from : vorgang.to;

  const geraet = vorgang.asset;
  const bezeichnung = geraet?.model_name ?? 'Gerät';

  return (
    <Link
      to="/geraete/$assetId"
      params={{ assetId: String(vorgang.asset_id) }}
      className="surface card-link block px-5 py-4 no-underline"
    >
      <div className="flex items-start gap-4">
        <span
          aria-hidden
          className={cn(
            'mt-0.5 grid h-11 w-11 shrink-0 place-items-center rounded-full',
            erhalten
              ? 'bg-[var(--color-khadi-forest-100)] text-[var(--color-khadi-forest-600)]'
              : 'bg-[var(--color-khadi-saffron-100)] text-[var(--color-khadi-saffron-600)]',
          )}
        >
          {erhalten ? (
            <ArrowDownLeft className="h-5 w-5" />
          ) : (
            <ArrowUpRight className="h-5 w-5" />
          )}
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="tabular text-sm font-semibold">{geraet?.asset_tag}</span>
            <span className="truncate text-[0.9375rem]">{bezeichnung}</span>
          </span>

          <span className="mt-1 block text-[0.9375rem] text-[var(--color-fg2)]">
            <strong className="font-semibold text-[var(--color-fg)]">
              {erhalten ? 'Erhalten' : 'Abgegeben'}
            </strong>
            {/* Wohin das Gerät gegangen ist, ist bei Abgängen die eigentliche
                Information — deshalb steht die Gegenstelle in derselben Zeile. */}
            {gegenstelle?.name && (
              <>
                {erhalten ? ' von ' : ' an '}
                <span className="text-[var(--color-fg)]">{gegenstelle.name}</span>
              </>
            )}
            {!gegenstelle?.name && vorgang.status_to && <> · {vorgang.status_to}</>}
          </span>

          {vorgang.note && (
            <span className="mt-1 block text-sm text-[var(--color-fg2)] italic">
              {vorgang.note}
            </span>
          )}

          <span className="mt-1 block text-sm text-[var(--color-fg3)]">
            {formatDateTime(vorgang.occurred_at)}
            {vorgang.actor ? ` · ${vorgang.actor}` : ''}
            {vorgang.source !== 'ui' ? ` · ${SOURCE_LABELS[vorgang.source] ?? vorgang.source}` : ''}
          </span>
        </span>
      </div>
    </Link>
  );
}
